import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, ShoppingBag, MapPin, Receipt, CircleNotch, Truck, CreditCard,
  Hash, User, Phone, EnvelopeSimple, ArrowSquareOut, CheckCircle, FileText,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = {
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  paid: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  shipped: 'bg-blue-100 text-blue-700 border-blue-300',
  delivered: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  processing: 'bg-amber-100 text-amber-700 border-amber-300',
  pending: 'bg-slate-100 text-slate-700 border-slate-300',
  cancelled: 'bg-red-100 text-red-700 border-red-300',
  refunded: 'bg-red-100 text-red-700 border-red-300',
  'on-hold': 'bg-amber-100 text-amber-700 border-amber-300',
};
const STATUS_FR = {
  completed: 'Terminée', paid: 'Payée', shipped: 'Expédiée',
  delivered: 'Livrée', processing: 'En cours', pending: 'En attente',
  cancelled: 'Annulée', refunded: 'Remboursée', 'on-hold': 'En attente',
};

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storeId, setStoreId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        const sid = me.data?.store_id || me.data?.stores?.[0]?.id;
        // Fallback: fetch first store
        let sId = sid;
        if (!sId) {
          const s = await axios.get(`${API_URL}/api/s/mrg`);
          sId = s.data.id;
        }
        setStoreId(sId);
        const { data } = await axios.get(`${API_URL}/api/stores/${sId}/orders/${orderId}`, { withCredentials: true });
        setOrder(data);
      } catch (e) {
        toast.error("Erreur de chargement de la commande");
      } finally { setLoading(false); }
    })();
  }, [orderId]);

  if (loading) return <div className="p-12 text-center"><CircleNotch size={32} className="animate-spin mx-auto text-slate-400" /></div>;
  if (!order) return <div className="p-12 text-center text-slate-500">Commande introuvable</div>;

  const fmt = (n) => `${(Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const fmtDate = (s) => s ? new Date(s).toLocaleString('fr-FR') : '—';
  const billing = order.billing_address || {};
  const shipping = order.shipping_address || {};
  const status = (order.status || '').toLowerCase();

  // Build a workflow timeline from order.history
  const STAGES = [
    { key: 'pending',    label: 'Bon de commande',     icon: Receipt,     color: 'slate' },
    { key: 'confirmed',  label: 'Confirmée / Payée',   icon: CreditCard,  color: 'blue' },
    { key: 'preparing',  label: 'Préparation',         icon: ShoppingBag, color: 'amber' },
    { key: 'shipping',   label: 'BL édité / Livraison', icon: Truck,      color: 'indigo' },
    { key: 'delivered',  label: 'Livrée',              icon: CheckCircle, color: 'emerald' },
    { key: 'invoiced',   label: 'Facturée',            icon: FileText,    color: 'violet' },
  ];
  const STAGE_ORDER = STAGES.map(s => s.key);

  // History entries keyed by status (most recent first)
  const historyByStatus = {};
  (order.history || []).forEach(h => {
    if (h?.status && !historyByStatus[h.status]) historyByStatus[h.status] = h;
  });
  // Auto-mark: pending => order.created_at, confirmed/paid => payment_status=paid
  if (!historyByStatus.pending) historyByStatus.pending = { date: order.created_at };
  if (!historyByStatus.confirmed && (order.payment_status === 'paid' || status === 'paid' || status === 'completed')) {
    historyByStatus.confirmed = { date: order.paid_at || order.created_at };
  }
  if (!historyByStatus.invoiced && order.invoice_url) {
    historyByStatus.invoiced = { date: order.invoiced_at || order.created_at };
  }
  const currentIdx = Math.max(...STAGE_ORDER.map((k, i) => historyByStatus[k] ? i : -1).filter(i => i >= 0), 0);

  const colorMap = {
    slate:    { bg: 'bg-slate-100',    text: 'text-slate-600',    ring: 'ring-slate-300',    bar: 'bg-slate-300' },
    blue:     { bg: 'bg-blue-100',     text: 'text-blue-700',     ring: 'ring-blue-400',     bar: 'bg-blue-500' },
    amber:    { bg: 'bg-amber-100',    text: 'text-amber-700',    ring: 'ring-amber-400',    bar: 'bg-amber-500' },
    indigo:   { bg: 'bg-indigo-100',   text: 'text-indigo-700',   ring: 'ring-indigo-400',   bar: 'bg-indigo-500' },
    emerald:  { bg: 'bg-emerald-100',  text: 'text-emerald-700',  ring: 'ring-emerald-400',  bar: 'bg-emerald-600' },
    violet:   { bg: 'bg-violet-100',   text: 'text-violet-700',   ring: 'ring-violet-400',   bar: 'bg-violet-500' },
  };

  return (
    <div className="p-6 space-y-4" data-testid="order-detail-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Hash size={20} className="text-slate-400" />
              {order.order_number}
              <Badge variant="outline" className={STATUS_COLORS[status] || 'bg-slate-100'}>{STATUS_FR[status] || status}</Badge>
            </h1>
            <p className="text-sm text-slate-500">Passée le {fmtDate(order.created_at)}{order.tracking_code ? ` · Suivi : ${order.tracking_code}` : ''}</p>
          </div>
        </div>
        {storeId && (
          <a href={`${API_URL}/api/stores/${storeId}/orders/${orderId}/invoice`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="gap-2" data-testid="invoice-btn">
              <ArrowSquareOut size={16} /> Télécharger facture
            </Button>
          </a>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Customer */}
        <Card>
          <CardContent className="p-4 space-y-2">
            <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2"><User size={18} className="text-slate-500" /> Client</h3>
            <p className="font-semibold text-slate-800">{order.customer_name || `${billing.first_name || ''} ${billing.last_name || ''}`}</p>
            {order.customer_email && (
              <p className="text-sm text-slate-600 flex items-center gap-1.5"><EnvelopeSimple size={14} /> {order.customer_email}</p>
            )}
            {(order.customer_phone || billing.phone) && (
              <p className="text-sm text-slate-600 flex items-center gap-1.5"><Phone size={14} /> {order.customer_phone || billing.phone}</p>
            )}
            {order.user_id && (
              <Link to={`/admin/customers/${order.user_id}`} className="text-[12px] text-[#0052FF] hover:underline mt-1 inline-block" data-testid="link-to-customer">
                Voir la fiche client →
              </Link>
            )}
          </CardContent>
        </Card>

        {/* Billing */}
        <Card>
          <CardContent className="p-4 space-y-1">
            <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2"><Receipt size={18} className="text-slate-500" /> Facturation</h3>
            <p className="text-sm font-semibold text-slate-800">{billing.first_name} {billing.last_name}</p>
            {billing.company && <p className="text-sm text-slate-600">{billing.company}</p>}
            <p className="text-sm text-slate-600">{billing.address_1 || billing.address || ''}</p>
            {billing.address_2 && <p className="text-sm text-slate-600">{billing.address_2}</p>}
            <p className="text-sm text-slate-600">{billing.postcode || billing.postal_code || ''} {billing.city}</p>
            <p className="text-sm text-slate-600">{billing.country}</p>
          </CardContent>
        </Card>

        {/* Shipping */}
        <Card>
          <CardContent className="p-4 space-y-1">
            <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2"><MapPin size={18} className="text-slate-500" /> Livraison</h3>
            <p className="text-sm font-semibold text-slate-800">{shipping.first_name} {shipping.last_name}</p>
            {shipping.company && <p className="text-sm text-slate-600">{shipping.company}</p>}
            <p className="text-sm text-slate-600">{shipping.address_1 || shipping.address || ''}</p>
            {shipping.address_2 && <p className="text-sm text-slate-600">{shipping.address_2}</p>}
            <p className="text-sm text-slate-600">{shipping.postcode || shipping.postal_code || ''} {shipping.city}</p>
            <p className="text-sm text-slate-600">{shipping.country}</p>
            {order.shipping_method && (
              <p className="text-sm text-slate-500 mt-2 flex items-center gap-1.5"><Truck size={14} /> {order.shipping_method}</p>
            )}
            {order.delivery_date && (
              <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
                📅 Livraison prévue le {fmtDate(order.delivery_date).split(' ')[0]}{order.delivery_slot ? ` · ${order.delivery_slot}` : ''}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Items table */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <ShoppingBag size={18} className="text-slate-500" /> Articles ({(order.items || []).length})
            </h3>
            <span className="text-[12px] text-slate-500 flex items-center gap-1.5">
              <CreditCard size={14} /> {order.payment_method || '—'}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Produit</th>
                <th className="text-left px-4 py-2">SKU</th>
                <th className="text-right px-4 py-2">Qté</th>
                <th className="text-right px-4 py-2">Prix unit.</th>
                <th className="text-right px-4 py-2">TVA</th>
                <th className="text-right px-4 py-2">Sous-total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(order.items || []).map((it, idx) => (
                <tr key={idx} className="hover:bg-slate-50" data-testid={`order-item-${idx}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{it.name}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-[12px]">{it.sku || '—'}</td>
                  <td className="px-4 py-2.5 text-right">{it.quantity}</td>
                  <td className="px-4 py-2.5 text-right">{fmt(it.price)}</td>
                  <td className="px-4 py-2.5 text-right text-[12px] text-slate-500">{it.tax_rate ? `${it.tax_rate}%` : '—'}</td>
                  <td className="px-4 py-2.5 text-right font-semibold">{fmt(it.total || it.subtotal || (it.price * it.quantity))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 text-slate-700">
              <tr><td colSpan={5} className="text-right px-4 py-1.5 text-[13px]">Sous-total</td><td className="text-right px-4 py-1.5">{fmt(order.subtotal)}</td></tr>
              {order.discount_amount > 0 && <tr><td colSpan={5} className="text-right px-4 py-1.5 text-[13px]">Remises</td><td className="text-right px-4 py-1.5 text-emerald-600">- {fmt(order.discount_amount)}</td></tr>}
              {order.shipping_cost >= 0 && <tr><td colSpan={5} className="text-right px-4 py-1.5 text-[13px]">Livraison</td><td className="text-right px-4 py-1.5">{order.shipping_cost > 0 ? fmt(order.shipping_cost) : 'Gratuit'}</td></tr>}
              {order.tax_amount > 0 && <tr><td colSpan={5} className="text-right px-4 py-1.5 text-[13px]">TVA</td><td className="text-right px-4 py-1.5">{fmt(order.tax_amount)}</td></tr>}
              <tr className="border-t-2 border-slate-200"><td colSpan={5} className="text-right px-4 py-2 font-bold">Total TTC</td><td className="text-right px-4 py-2 font-black text-[16px]">{fmt(order.total)}</td></tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      {/* Visual Timeline */}
      <Card>
        <CardContent className="p-6">
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <CircleNotch size={18} /> Suivi de la commande
          </h3>
          <div className="flex items-start justify-between gap-2 overflow-x-auto px-2 pt-3 pb-4">
            {STAGES.map((stage, idx) => {
              const reached = idx <= currentIdx;
              const isCurrent = idx === currentIdx;
              const entry = historyByStatus[stage.key];
              const c = colorMap[stage.color];
              return (
                <React.Fragment key={stage.key}>
                  <div className="flex flex-col items-center text-center min-w-[110px] flex-1" data-testid={`stage-${stage.key}`}>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${reached ? c.bg + ' ' + c.text : 'bg-slate-50 text-slate-300'} ${isCurrent ? 'ring-2 ring-offset-2 ' + c.ring : ''}`}>
                      <stage.icon size={22} weight={reached ? 'fill' : 'regular'} />
                    </div>
                    <p className={`text-[12px] font-semibold mt-2 ${reached ? c.text : 'text-slate-400'}`}>
                      {stage.label}
                    </p>
                    {entry?.date && (
                      <p className="text-[10px] text-slate-400 mt-0.5">{fmtDate(entry.date)}</p>
                    )}
                    {entry?.user_name && (
                      <p className="text-[9px] text-slate-400">par {entry.user_name}</p>
                    )}
                  </div>
                  {idx < STAGES.length - 1 && (
                    <div className={`h-1 rounded flex-1 mt-[26px] mx-0 ${idx < currentIdx ? colorMap[STAGES[idx + 1].color].bar : 'bg-slate-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {/* Quick links */}
          <div className="mt-5 pt-4 border-t flex flex-wrap gap-2">
            {order.bl_url && (
              <a href={order.bl_url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200"
                 data-testid="link-bl">
                <FileText size={14} /> Bon de livraison
              </a>
            )}
            {order.invoice_url && (
              <a href={order.invoice_url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] rounded-md bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200"
                 data-testid="link-invoice">
                <Receipt size={14} /> Voir la facture
              </a>
            )}
            {order.delivery_proof?.signature_url && (
              <a href={`${API_URL}${order.delivery_proof.signature_url}`} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200">
                <CheckCircle size={14} /> Signature client
              </a>
            )}
            {order.delivery_proof?.photo_url && (
              <a href={`${API_URL}${order.delivery_proof.photo_url}`} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200">
                <CheckCircle size={14} /> Photo livraison
              </a>
            )}
          </div>
        </CardContent>
      </Card>

      {/* History */}
      {(order.history || []).length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-bold text-slate-800 mb-3">Historique détaillé</h3>
            <ul className="space-y-1.5 text-sm">
              {order.history.map((h, idx) => (
                <li key={idx} className="flex items-center gap-2">
                  <span className="text-slate-400 text-[12px]">{fmtDate(h.date)}</span>
                  <Badge variant="outline" className={STATUS_COLORS[h.status] || ''}>{STATUS_FR[h.status] || h.status}</Badge>
                  <span className="text-slate-600">{h.notes}</span>
                  {h.user_name && <span className="text-[11px] text-slate-400">par {h.user_name}</span>}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OrderDetailPage;
